import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTachometerAlt, 
  faHistory, 
  faWeightHanging, 
  faBolt, 
  faCog, 
  faMicrochip, 
  faSatelliteDish, 
  faUsers, 
  faBan, 
  faQuestionCircle, 
  faWaveSquare, 
  faTemperatureHigh, 
  faExclamationTriangle, 
  faFileExport, 
  faBell,
  faBars
} from '@fortawesome/free-solid-svg-icons';
import '../scss/AppSidebar.scss';

const AppSidebar = ({ onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState({});
  const sidebarRef = useRef(null);
  const location = useLocation();

  const navItems = [
    { path: '/dashboard', icon: faTachometerAlt, label: 'Dashboard' },
    { path: '/operationslog', icon: faHistory, label: 'Operations Log' },
    { path: '/load', icon: faWeightHanging, label: 'Load' },
    { path: '/energy-monitoring', icon: faBolt, label: 'Energy Monitoring' },
    {
      type: 'dropdown',
      icon: faCog,
      label: 'Settings',
      items: [
        { path: '/machine', icon: faWeightHanging, label: 'Machine' },
        { path: '/device', icon: faMicrochip, label: 'Device' },
        { path: '/remote', icon: faSatelliteDish, label: 'Remote' },
        { path: '/users', icon: faUsers, label: 'Users' }
      ]
    },
    { type: 'divider', label: 'Under Development' },
    { path: '/brake', icon: faBan, label: 'Brake Monitoring' },
    { path: '/zone', icon: faQuestionCircle, label: 'Zone' },
    { path: '/vibration-monitoring', icon: faWaveSquare, label: 'Vibration Monitoring' },
    { path: '/temperature', icon: faTemperatureHigh, label: 'Temperature' },
    { path: '/error-log', icon: faExclamationTriangle, label: 'Error Log' },
    { path: '/reports', icon: faFileExport, label: 'Reports' },
    { path: '/alerts', icon: faBell, label: 'Alerts' },
    { path: '/help', icon: faQuestionCircle, label: 'Help' }
  ];

  // Function to notify parent about sidebar state change
  const notifyToggle = useCallback((expanded) => {
    if (onToggle) {
      onToggle(expanded);
    }
    window.dispatchEvent(new CustomEvent('sidebarToggle', { 
      detail: { isExpanded: expanded } 
    }));
  }, [onToggle]);

  // Toggle sidebar expansion (click on hamburger)
  const toggleSidebar = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    setIsHovered(false); // Reset hover state when toggling
    notifyToggle(newExpandedState); // Only notify on explicit click
    
    if (!newExpandedState) {
      setOpenDropdowns({});
    }
  };

  // Handle hover for temporary expansion
  const handleMouseEnter = () => {
    if (!isExpanded) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isExpanded && isHovered) {
      setIsHovered(false);
    }
  };

  // Toggle dropdown
  const toggleDropdown = (dropdownKey, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (window.innerWidth <= 768 && !isExpanded) {
      setIsExpanded(true);
      setIsHovered(false);
      notifyToggle(true); // Keep this for mobile
      setTimeout(() => {
        setOpenDropdowns(prev => ({
          ...prev,
          [dropdownKey]: !prev[dropdownKey]
        }));
      }, 50);
      return;
    }

    setOpenDropdowns(prev => ({
      ...prev,
      [dropdownKey]: !prev[dropdownKey]
    }));
  };

  // Close all dropdowns
  const closeAllDropdowns = () => {
    setOpenDropdowns({});
  };

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (window.innerWidth <= 768) {
        if (event.target.closest('.dropdown-toggle') || event.target.closest('.dropdown-menu')) {
          return;
        }

        if (sidebarRef.current && !sidebarRef.current.contains(event.target) && isExpanded) {
          setIsExpanded(false);
          setIsHovered(false);
          notifyToggle(false);
          closeAllDropdowns();
        }
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isExpanded, notifyToggle]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutsideDropdown = (event) => {
      if (!event.target.closest('.dropdown')) {
        closeAllDropdowns();
      }
    };

    document.addEventListener('click', handleClickOutsideDropdown);
    return () => document.removeEventListener('click', handleClickOutsideDropdown);
  }, []);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        if (!isExpanded) {
          setIsHovered(false);
          // Keep this to ensure proper state on resize
          if (onToggle) onToggle(false);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isExpanded, onToggle]);

  // Check if a dropdown should be open based on current route
  const isDropdownActive = (dropdownItems) => {
    return dropdownItems.some(item => item.path === location.pathname);
  };

  // Check if a route is active (keep as is)
  const isActiveRoute = (path) => {
    return location.pathname === path;
  };

  // Determine if sidebar should be visually expanded
  const shouldBeExpanded = isExpanded || isHovered;

  return (
    <div 
      ref={sidebarRef}
      className={`sidebar ${shouldBeExpanded ? 'expanded' : ''}`}
      id="sidebar"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <button className="sidebar-toggle" id="sidebarToggle" onClick={toggleSidebar}>
        <FontAwesomeIcon icon={faBars} />
      </button>

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {navItems.map((item, index) => {
            if (item.type === 'divider') {
              return (
                <li key={index} className="nav-divider">
                  <div className="divider-text">{item.label}</div>
                </li>
              );
            }
            
            if (item.type === 'dropdown') {
              const isOpen = openDropdowns[item.label] || (isDropdownActive(item.items) && shouldBeExpanded);
              
              return (
                <li key={index} className="nav-item dropdown">
                  <button
                    type="button"
                    className={`nav-link dropdown-toggle ${isOpen ? 'show' : ''}`}
                    onClick={(e) => toggleDropdown(item.label, e)}
                  >
                    <FontAwesomeIcon icon={item.icon} />
                    <span>{item.label}</span>
                  </button>
                  <div className={`dropdown-menu ${isOpen ? 'show' : ''}`}>
                    {item.items.map((subItem, subIndex) => (
                      <Link 
                        key={subIndex}
                        to={subItem.path}
                        className={`dropdown-item ${isActiveRoute(subItem.path) ? 'active' : ''}`}
                        onClick={() => {
                          if (window.innerWidth <= 768) {
                            setIsExpanded(false);
                            setIsHovered(false);
                            notifyToggle(false);
                            closeAllDropdowns();
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={subItem.icon} />
                        <span>{subItem.label}</span>
                      </Link>
                    ))}
                  </div>
                </li>
              );
            }
            
            return (
              <li key={index} className="nav-item">
                <Link 
                  to={item.path} 
                  className={`nav-link ${isActiveRoute(item.path) ? 'active' : ''}`}
                  onClick={() => {
                    if (window.innerWidth <= 768) {
                      setIsExpanded(false);
                      setIsHovered(false);
                      notifyToggle(false);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={item.icon} />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default AppSidebar;